/**
 * trendContextScore — universal trend-alignment score on every trade.
 *
 * A single 0–5 number describing how well-aligned the entry was with
 * prevailing trend, across multiple lenses. The most-cited concept
 * across the educator research (Brooks always-in, Shannon multi-TF,
 * Grimes with-trend, Raschke ADX gate, Diaz HTF EMA filter) — every
 * analyzer downstream can filter on it.
 *
 * Score composition (1 point each, max 5):
 *   1. 5m EMA(20) slope at entry agrees with trade direction
 *   2. 15m EMA(20) slope at entry agrees with trade direction
 *   3. 1h EMA(20) slope at entry agrees with trade direction
 *   4. 4h EMA(20) slope at entry agrees with trade direction
 *   5. ADX(14) on 5m at entry > 25 (trending regime, regardless of side)
 *
 * Slopes are computed as: EMA(now) − EMA(N bars ago), default N=5. Sign
 * determines direction. Magnitude is not used here — this is a 0/1 vote.
 *
 * Returns:
 *   {
 *     score:        number  // 0–5
 *     maxScore:     number  // 5 normally; lower if some HTF bars missing
 *                            // ("had partial TF data; couldn't evaluate 4h")
 *     reasons:      string[]  // human-readable contributors (for UI)
 *     gaps:         string[]  // TFs that were unavailable for evaluation
 *     adxAtEntry:   number|null  // for downstream observations / debug
 *   }
 *
 * Returns null if there isn't enough data to score anything meaningful
 * (no 5m bars, or warm-up not satisfied).
 *
 * Pure function. No side effects. Consumers store the result on the
 * trade's `barAnalysis.trendContextScore` field via maeMfe.js.
 */

import { computeEMA } from './emas.js';
import { computeADX, adxAtTime } from './adx.js';

// Trend EMA period to use as the slope reference. 20 is the Brooks /
// SMB / Shannon convention and is what every "with-trend / against-trend"
// piece of educator literature defaults to. Changeable via options.
export const DEFAULT_TREND_EMA = 20;

// Lookback (in bars) for slope direction. 5 bars on any timeframe gives
// enough averaging to ignore single-bar noise without lagging the slope
// reading too far behind real trend changes.
export const DEFAULT_SLOPE_LOOKBACK = 5;

// ADX threshold for the "trending regime" point. 25 is the Raschke +
// Wilder convention. Higher (30+) yields a stricter score that fires
// less often but is more reliable when it does.
export const DEFAULT_ADX_THRESHOLD = 25;

// HTF timeframes the score evaluates, in order. The 5m TF is implicit
// (uses `entryBars`); these are the EXTRA timeframes pulled from
// `htfBars` map. Missing TFs lower `maxScore` but don't error.
export const DEFAULT_HTF_LABELS = ['15m', '1h', '4h'];

/**
 * Locate the LAST bar with time ≤ entryTs (the bar containing the entry
 * under open-time semantics). Returns -1 when no bar qualifies.
 */
const findEntryBarIdx = (bars, entryTs) => {
  if (!Array.isArray(bars) || bars.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) idx = i;
    else break;
  }
  return idx;
};

/**
 * EMA slope direction at the entry bar: +1 if rising, -1 if falling,
 * 0 if flat or undefined (warm-up).
 */
const emaSlopeSign = (bars, period, entryIdx, lookback) => {
  if (entryIdx < 0) return 0;
  const idxPrev = entryIdx - lookback;
  if (idxPrev < 0) return 0;
  const series = computeEMA(bars, period);
  const a = series[idxPrev]?.value;
  const b = series[entryIdx]?.value;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  const delta = b - a;
  if (delta > 0) return 1;
  if (delta < 0) return -1;
  return 0;
};

/**
 * Normalize trade direction to a sign: long → +1, short → -1, else 0.
 * Returns 0 for unknown/empty so the score becomes "can't evaluate".
 */
const directionSign = (direction) => {
  const d = String(direction || '').toLowerCase().trim();
  if (d === 'long' || d === 'buy' || d === 'l') return 1;
  if (d === 'short' || d === 'sell' || d === 's') return -1;
  return 0;
};

/**
 * Main entry point.
 *
 * @param {Object} params
 * @param {Array}  params.entryBars   5m OHLCV bars covering the entry. Required.
 * @param {Object} [params.htfBars]   Optional map of TF label → bars. e.g. { '15m': [...], '1h': [...], '4h': [...] }
 * @param {number} params.entryTs     Unix seconds. Required.
 * @param {string} params.direction   'long' | 'short' | aliases. Required.
 * @param {Object} [params.options]   Overrides (trendEma, slopeLookback, adxThreshold, htfLabels).
 * @returns {Object|null}  { score, maxScore, reasons, gaps, adxAtEntry } or null when un-scorable.
 */
export const computeTrendContextScore = ({
  entryBars,
  htfBars = {},
  entryTs,
  direction,
  options = {},
} = {}) => {
  const sign = directionSign(direction);
  if (!sign) return null;
  if (!Number.isFinite(entryTs)) return null;
  if (!Array.isArray(entryBars) || entryBars.length === 0) return null;

  const trendEma = options.trendEma || DEFAULT_TREND_EMA;
  const lookback = options.slopeLookback || DEFAULT_SLOPE_LOOKBACK;
  const adxThr = options.adxThreshold || DEFAULT_ADX_THRESHOLD;
  const htfLabels = Array.isArray(options.htfLabels) ? options.htfLabels : DEFAULT_HTF_LABELS;

  const reasons = [];
  const gaps = [];
  let score = 0;
  let maxScore = 0;

  // ── 1. 5m EMA(20) slope at entry ────────────────────────────────
  const entryIdx5m = findEntryBarIdx(entryBars, entryTs);
  const slope5m = emaSlopeSign(entryBars, trendEma, entryIdx5m, lookback);
  if (slope5m !== 0) {
    maxScore++;
    if (slope5m === sign) {
      score++;
      reasons.push(`5m EMA${trendEma} slope aligned`);
    }
  } else {
    gaps.push('5m');
  }

  // ── 2–4. HTF EMA(20) slopes ────────────────────────────────────
  for (const tf of htfLabels) {
    const tfBars = htfBars?.[tf];
    if (!Array.isArray(tfBars) || tfBars.length === 0) {
      gaps.push(tf);
      continue;
    }
    const tfIdx = findEntryBarIdx(tfBars, entryTs);
    const tfSlope = emaSlopeSign(tfBars, trendEma, tfIdx, lookback);
    if (tfSlope === 0) {
      gaps.push(tf);
      continue;
    }
    maxScore++;
    if (tfSlope === sign) {
      score++;
      reasons.push(`${tf} EMA${trendEma} slope aligned`);
    }
  }

  // ── 5. ADX(14) on 5m at entry > threshold ──────────────────────
  let adxAtEntry = null;
  try {
    const adxSeries = computeADX(entryBars, 14);
    adxAtEntry = adxAtTime(adxSeries, entryTs);
  } catch {
    // ADX failure shouldn't poison the score — just skip the trending point.
  }
  if (Number.isFinite(adxAtEntry)) {
    maxScore++;
    if (adxAtEntry > adxThr) {
      score++;
      reasons.push(`ADX(${adxAtEntry.toFixed(1)}) trending`);
    }
  } else {
    gaps.push('adx');
  }

  // Edge case: nothing was evaluable. Return null so analyzers know
  // this trade has no trend-context signal at all.
  if (maxScore === 0) return null;

  return { score, maxScore, reasons, gaps, adxAtEntry };
};
